import { useId, useState } from "react";

export type CurvePoint = { setNumber: number; value: number };

type Props = {
    points: CurvePoint[];
    /** The scale's ends, so 4 always sits at the same height. */
    min: number;
    max: number;
    ticks: number[];
    /**
     * Every set of the session, in order: the x axis. Shared by the SUD and
     * VOC charts, so set 4 sits at the same place in both even when set 3 has
     * a SUD and no VOC.
     */
    setNumbers: number[];
    /** Names the chart: there is one series, so no legend — the title says what it is. */
    title: string;
    /** Which way is better, in words: "lower is better". */
    hint?: string;
    /** "SUD", "VOC": what the tooltip calls a value. */
    unit: string;
    /** How a set is named on its own: "Set {{n}}". */
    setLabel: (setNumber: number) => string;
};

const WIDTH = 320;
const HEIGHT = 140;
const PAD = { top: 12, right: 14, bottom: 24, left: 26 };

/**
 * One rating across the session's sets: a line on its own fixed scale.
 *
 * SUD and VOC are drawn as two of these rather than one chart with two axes —
 * 0–10 and 1–7 on one plot would make their crossing mean something it does
 * not. Each point answers on hover or focus; the numbers are in the session's
 * table too, for anyone who cannot see the line.
 */
export default function SetCurve({ points, min, max, ticks, setNumbers, title, hint, unit, setLabel }: Props) {
    const id = useId();
    const [active, setActive] = useState<number | null>(null);
    if (points.length < 2) return null;

    const innerW = WIDTH - PAD.left - PAD.right;
    const innerH = HEIGHT - PAD.top - PAD.bottom;
    const first = setNumbers[0] ?? points[0].setNumber;
    const last = setNumbers.at(-1) ?? points.at(-1)!.setNumber;
    const span = Math.max(1, last - first);
    const xOf = (setNumber: number) => PAD.left + ((setNumber - first) / span) * innerW;
    const x = (i: number) => xOf(points[i].setNumber);
    const y = (value: number) => PAD.top + (1 - (value - min) / (max - min)) * innerH;
    const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
    const description = points.map((p) => `${setLabel(p.setNumber)}: ${unit} ${p.value}`).join(", ");
    const shown = active === null ? null : points[active];

    return (
        <figure className="flex min-w-0 flex-col gap-2" aria-labelledby={`${id}-title`}>
            <figcaption className="flex flex-wrap items-baseline justify-between gap-2">
                <span id={`${id}-title`} className="text-sm font-semibold text-content-primary">
                    {title}
                </span>
                {hint && <span className="text-xs text-content-tertiary">{hint}</span>}
            </figcaption>
            <div className="relative">
                <svg
                    viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                    role="img"
                    aria-label={title}
                    aria-describedby={`${id}-desc`}
                    className="h-auto w-full text-accent-text"
                    onMouseLeave={() => setActive(null)}
                >
                    {ticks.map((tick) => (
                        <g key={tick}>
                            <line
                                x1={PAD.left}
                                x2={WIDTH - PAD.right}
                                y1={y(tick)}
                                y2={y(tick)}
                                className="stroke-border-subtle"
                                strokeWidth={1}
                            />
                            <text x={PAD.left - 6} y={y(tick) + 3} textAnchor="end" className="fill-content-tertiary text-[9px]">
                                {tick}
                            </text>
                        </g>
                    ))}
                    {active !== null && (
                        <line
                            x1={x(active)}
                            x2={x(active)}
                            y1={PAD.top}
                            y2={HEIGHT - PAD.bottom}
                            className="stroke-border-subtle"
                            strokeWidth={1}
                            strokeDasharray="3 3"
                        />
                    )}
                    {setNumbers.map((n) => (
                        <text key={n} x={xOf(n)} y={HEIGHT - 8} textAnchor="middle" className="fill-content-tertiary text-[9px]">
                            {n}
                        </text>
                    ))}
                    <path d={path} fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                    {points.map((p, i) => (
                        <g key={p.setNumber}>
                            {/* Ringed in the surface colour, so a point on the line stays a point. */}
                            <circle
                                cx={x(i)}
                                cy={y(p.value)}
                                r={active === i ? 5.5 : 4}
                                fill="currentColor"
                                className="stroke-surface-raised"
                                strokeWidth={2}
                            />
                            {/* A target larger than the mark, for the pointer and the keyboard. */}
                            <circle
                                cx={x(i)}
                                cy={y(p.value)}
                                r={14}
                                fill="transparent"
                                tabIndex={0}
                                aria-label={`${setLabel(p.setNumber)}: ${unit} ${p.value}`}
                                onMouseEnter={() => setActive(i)}
                                onFocus={() => setActive(i)}
                                onBlur={() => setActive(null)}
                                className="cursor-default outline-none"
                            />
                        </g>
                    ))}
                </svg>
                {shown && active !== null && (
                    <div
                        role="status"
                        className="pointer-events-none absolute -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-theme-md dark:bg-gray-700"
                        style={{ left: `${(x(active) / WIDTH) * 100}%`, top: `calc(${(y(shown.value) / HEIGHT) * 100}% - 10px)` }}
                    >
                        {setLabel(shown.setNumber)} · {unit} {shown.value}
                    </div>
                )}
            </div>
            <p id={`${id}-desc`} className="sr-only">
                {description}
            </p>
        </figure>
    );
}
